import { ConsentProviderInterface } from "./consent-provider-interface";
import { Consents } from "../types";
import { inject, injectWithTransform, singleton } from "tsyringe";
import { CmpApi } from "@iabtcf/cmpapi";
import { Logger, LoggerTransformer } from "@identity/utils";
import { WINDOW } from "@identity/symbols";

@singleton()
export class TcfApi implements ConsentProviderInterface {
  consentGiven: Promise<Consents>;
  private resolveConsent: (consent: Consents) => void;
  private IAB_PURPOSES_COUNT = 10;
  private TCF_API_VERSION = 2;

  private CMP_VERSION = 5; // Increment to force modal again
  private CMP_ID = 141;

  constructor(
    @inject(WINDOW) private window: Window,
    @injectWithTransform(Logger, LoggerTransformer, "TCF")
    private logger: Logger
  ) {
    this.initializeTcfApi();
    this.consentGiven = new Promise((res) => (this.resolveConsent = res));
    this.readTrackingConsents();
    this.subscribe();
  }

  private initializeTcfApi(): void {
    if (
      this.window.__uspapi === undefined &&
      this.window.__tcfapi === undefined
    ) {
      new CmpApi(this.CMP_ID, this.CMP_VERSION, true);
    }
  }

  private subscribe(): void {
    this.window.__tcfapi &&
      this.window.__tcfapi(
        "addEventListener",
        this.TCF_API_VERSION,
        this.handleTcfSubscriptionResponse.bind(this)
      );
  }

  private handleTcfSubscriptionResponse(tcdata: TcfResponse) {
    if (
      tcdata?.eventStatus === "useractioncomplete" ||
      tcdata?.eventStatus === "tcloaded"
    ) {
      const consent = this.detectTcfTrackingConsent(tcdata);
      this.consentsUpdated(consent);
      this.window.__tcfapi(
        "removeEventListener",
        this.TCF_API_VERSION,
        null,
        tcdata.listenerId
      );
    }
  }

  private readTrackingConsents(): void {
    this.window.__tcfapi &&
      this.window.__tcfapi(
        "getTCData",
        this.TCF_API_VERSION,
        this.handleReadConsentsResponse.bind(this)
      );
  }

  private handleReadConsentsResponse(tcdata: TcfResponse) {
    if (
      tcdata?.eventStatus === "useractioncomplete" ||
      tcdata?.eventStatus === "tcloaded"
    ) {
      const consent = this.detectTcfTrackingConsent(tcdata);
      this.consentsUpdated(consent);
    }
  }

  public detectTcfTrackingConsent(tcdata: TcfResponse): boolean {
    if (tcdata?.purpose) {
      return (
        Object.keys(tcdata.purpose.consents)
          .map((key) => tcdata.purpose.consents[key])
          .reduce(
            (previousValue, currentValue) => currentValue + previousValue,
            0
          ) === this.IAB_PURPOSES_COUNT
      );
    } else {
      return false;
    }
  }

  consentsUpdated(consent: boolean): void {
    this.logger.info("Consents ready");
    this.resolveConsent({
      gdprConsent: consent,
      geoRequiresConsent: true,
      geoRequiresSignal: false,
      ccpaSignal: false,
    });
  }

  public getTCData(version?: number): Promise<TCData> {
    return new Promise((resolve) => {
      if (this.window.__tcfapi) {
        this.window.__tcfapi(
          "getTCData",
          version || this.TCF_API_VERSION,
          (tcData) => resolve(tcData)
        );
      } else {
        resolve({
          gdprApplies: false,
        });
      }
    });
  }
}
